<script setup>
import {onMounted, ref} from 'vue'
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net';
import Provider from '@/api/Provider.js'

// Import components
import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'
import ChangeIndicator from '@/components/Anonymization/ChangeIndicator.vue'
import OptionsMenu from '@/components/Anonymization/OptionsMenu.vue'

DataTable.use(DataTablesCore)

let data = ref([])

const columns = [
  { data: 'name', title: '', className: 'thumbnail-col', orderable: false, width: '40px' },
  { data: 'name', title: 'Provider', className: 'provider-name-col' },
  //{ data: 'country', title: 'Country', className: 'country-col' },
  //{ data: 'types', title: 'Types', className: 'types-col' },
  { data: 'delta_week', name:"delta_week", title: 'Last Week', className: 'change-col' },
  { data: 'delta_day', name:"delta_day", title: '24H CHG', className: 'change-col' },
  { data: 'total', title: 'TOTAL', className: 'total-col' },
  { data: null, name:"options", title: '', className: 'options-col', orderable: false, width: '30px' }
];

const options = {
    pagingType: 'simple_numbers',
    paging: false,
    scrollCollapse: true,
    scrollY: '600px',
    scrollX: false,
    layout: {
        topStart: null,
        topEnd: null,
        bottomStart: null,
        bottomEnd: null
    },
    order: [[3, 'desc']], // Sort by TOTAL column (now index 3)
    columnDefs: [
      { targets: 3, className: 'text-right' }, // TOTAL column
      { targets: 4, className: 'text-right', orderable: false } // Options column
    ]
}

onMounted(async () => {
  data.value = Object.values((await Provider.providerStats()))
})
</script>

<template>
  <div class="box padded">
    <div class="provider-table-container">
      <DataTable
        v-if="data.length > 0"
        :columns="columns"
        :options="options"
        :data="data"
        class="provider-datatable"
      >
        <template #column-0="props">
          <ProviderThumbnail :data="props.cellData"/>
        </template>

        <template #column-name="props">
          <div class="provider-name">{{ props.cellData }}</div>
        </template>

        <template #column-delta_week="props">
          <ChangeIndicator :data="props.cellData"/>
        </template>

        <template #column-delta_day="props">
          <ChangeIndicator :data="props.cellData"/>
        </template>

        <template #column-total="props">
          <div class="total-value">
            {{ props.cellData >= 1000 ? (props.cellData / 1000).toFixed(0) + 'K' : props.cellData }}
          </div>
        </template>

        <template #column-options="props">
          <OptionsMenu :data="props.cellData"/>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<style scoped>
/* Table container styling */
.provider-table-container {
  background-color: transparent;
  overflow: hidden;
  height: 650px;
  max-height: 800px;
}

/* Container styles */
.provider-table-container :deep(.dt-container) {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: transparent;
}

/* Header styling - smaller and muted, no dividers */
.provider-table-container :deep(.provider-datatable thead th) {
  background-color: transparent !important;
  color: #6B7280 !important;
  font-size: 0.7rem !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 0.5rem 0.75rem !important;
  border: none !important;
}

/* Body styling - no dividers */
.provider-table-container :deep(.provider-datatable tbody td) {
  background-color: transparent !important;
  color: #E5E7EB !important;
  font-size: 0.85rem !important;
  padding: 0.75rem !important;
  border: none !important;
}

.provider-table-container :deep(.provider-datatable tbody tr:hover td) {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

/* Thumbnail column */
.provider-table-container :deep(.thumbnail-col) {
  width: 40px !important;
  text-align: center !important;
}

/* Provider name column */
.provider-table-container :deep(.provider-name-col) {
  font-weight: 500 !important;
  color: #FFFFFF !important;
}

.provider-name {
  font-weight: 500;
  color: #FFFFFF;
}

.total-value {
  font-weight: 500;
  color: #E5E7EB;
  text-align: right;
}

/* Total column */
.provider-table-container :deep(.total-col) {
  font-weight: 500 !important;
  color: #E5E7EB !important;
  text-align: right !important;
}

/* Options column */
.provider-table-container :deep(.options-col) {
  text-align: right !important;
  width: 30px !important;
}

/* Override dropdown menu styling within the table */
:deep(.dropdown-menu) {
  background-color: #1E252F !important;
  border: 1px solid #374151 !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.1) !important;
  min-width: 160px !important;
  margin-top: 0.25rem !important;
  padding: 0.5rem 0 !important;
}

:deep(.dropdown-item) {
  color: #D1D5DB !important;
  font-size: 0.85rem !important;
  padding: 0.75rem 1rem !important;
  display: flex !important;
  align-items: center !important;
  transition: all 0.15s ease-in-out !important;
  border: none !important;
  background: none !important;
}

:deep(.dropdown-item:hover) {
  background-color: #374151 !important;
  color: #FFFFFF !important;
}

:deep(.dropdown-item:focus) {
  background-color: #374151 !important;
  color: #FFFFFF !important;
  outline: none !important;
}

.provider-table-container :deep(.text-right) {
  text-align: right !important;
}

/* Scroll styling */
.provider-table-container :deep(.dt-scroll-body) {
  background-color: transparent !important;
  max-height: 600px !important;
}

.provider-table-container :deep(.dt-scroll-head) {
  background-color: transparent !important;
}

/* Remove default DataTable borders and styling */
.provider-table-container :deep(.provider-datatable) {
  border: none !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

/* Hide sorting arrows completely to fix positioning issues */
.provider-table-container :deep(.dt-column-order) {
  display: none !important;
}

/* Hide default DataTable sorting indicators */
.provider-table-container :deep(.dt-orderable-asc),
.provider-table-container :deep(.dt-orderable-desc),
.provider-table-container :deep(.dt-ordering-asc),
.provider-table-container :deep(.dt-ordering-desc) {
  padding-right: inherit !important;
}

.provider-table-container :deep(.dt-orderable-asc .dt-column-order),
.provider-table-container :deep(.dt-orderable-desc .dt-column-order),
.provider-table-container :deep(.dt-ordering-asc .dt-column-order),
.provider-table-container :deep(.dt-ordering-desc .dt-column-order) {
  display: none !important;
}



/* Scrollbar styling */
.provider-table-container :deep(.dt-scroll-body)::-webkit-scrollbar {
  width: 4px;
}

.provider-table-container :deep(.dt-scroll-body)::-webkit-scrollbar-track {
  background: transparent;
}

.provider-table-container :deep(.dt-scroll-body)::-webkit-scrollbar-thumb {
  background: #3E4A56;
  border-radius: 2px;
}

.provider-table-container :deep(.dt-scroll-body)::-webkit-scrollbar-thumb:hover {
  background: #4A5661;
}

/* Dropdown menu styling - improved dark mode */
.provider-table-container :deep(.dropdown-menu) {
  background-color: #1E252F !important;
  border: 1px solid #374151 !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.1) !important;
  min-width: 160px !important;
  margin-top: 0.25rem !important;
  padding: 0.5rem 0 !important;
}

.provider-table-container :deep(.dropdown-item) {
  color: #D1D5DB !important;
  font-size: 0.85rem !important;
  padding: 0.75rem 1rem !important;
  display: flex !important;
  align-items: center !important;
  transition: all 0.15s ease-in-out !important;
  border: none !important;
  background: none !important;
}

.provider-table-container :deep(.dropdown-item:hover) {
  background-color: #374151 !important;
  color: #FFFFFF !important;
}

.provider-table-container :deep(.dropdown-item:focus) {
  background-color: #374151 !important;
  color: #FFFFFF !important;
  outline: none !important;
}

.provider-table-container :deep(.dropdown-item i) {
  width: 16px !important;
  text-align: center !important;
  margin-right: 0.5rem !important;
  color: inherit !important;
}

/* Fix dropdown positioning */
.provider-table-container :deep(.dropdown-menu-end) {
  right: 0 !important;
  left: auto !important;
}
</style>
